/**
 * The peek trace (1.I-2 deliverable 29), modelled on 1.X's open trace: User Timing marks
 * `codex.peek.<phase>` along one open of a side peek, a center peek or pane 2, and `peekPhases`,
 * which turns them into the time each phase waited.
 *
 * The phases, in the order an open passes them: `open` (`OpenModes`' `open()`), `fetch`
 * (`docs.get` sent), `chunk` (the pane module resolved), `answer` (`docs.get` answered),
 * `preview` (the preview painted), `session` (the sync session opened), `editor` (the editor's
 * first paint) and `ready` (`data-editor-ready`). `open()` clears the previous open's marks first.
 *
 * Pure apart from `markPeek`, `clearPeekTrace`, `peekMarks` and `afterNextPaint`, which only
 * write and read User Timing entries and wait for a frame (no secure-context API). Nothing here
 * reads a body, a title or any document text.
 */

export const PEEK_PHASES = [
  'open',
  'fetch',
  'chunk',
  'answer',
  'preview',
  'session',
  'editor',
  'ready',
] as const;
export type PeekPhase = (typeof PEEK_PHASES)[number];

export const PEEK_MARK_PREFIX = 'codex.peek.';

export const peekMarkName = (phase: PeekPhase): string => `${PEEK_MARK_PREFIX}${phase}`;

/** A mark as User Timing hands it back (`PerformanceMark` is one). */
export interface PeekMark {
  readonly name: string;
  readonly startTime: number;
}

export interface PeekPhases {
  /**
   * Per phase present: the time from the latest earlier phase (in the open's order) that had
   * already happened when it did. A warmed chunk resolves before the answer, an answer may come
   * before the chunk: each gap names what that phase waited for, and none is negative.
   */
  readonly gaps: Partial<Record<PeekPhase, number>>;
  /** Per phase present: the time from `open` (from the first mark when `open` is missing). */
  readonly since: Partial<Record<PeekPhase, number>>;
  /** From the first mark present to the last. */
  readonly total: number;
}

const isPhase = (name: string): name is PeekPhase =>
  (PEEK_PHASES as readonly string[]).includes(name);

/** The gaps of one open's marks (the newest mark of each phase wins). */
export function peekPhases(marks: readonly PeekMark[]): PeekPhases {
  const at = new Map<PeekPhase, number>();
  for (const mark of marks) {
    if (!mark.name.startsWith(PEEK_MARK_PREFIX)) continue;
    const phase = mark.name.slice(PEEK_MARK_PREFIX.length);
    if (!isPhase(phase) || !Number.isFinite(mark.startTime)) continue;
    at.set(phase, mark.startTime);
  }
  const times = [...at.values()];
  const first = times.length === 0 ? 0 : Math.min(...times);
  const origin = at.get('open') ?? first;
  const gaps: Partial<Record<PeekPhase, number>> = {};
  const since: Partial<Record<PeekPhase, number>> = {};
  const seen: number[] = [];
  for (const phase of PEEK_PHASES) {
    const time = at.get(phase);
    if (time === undefined) continue;
    const before = seen.filter((t) => t <= time);
    gaps[phase] = before.length === 0 ? 0 : time - Math.max(...before);
    since[phase] = Math.max(0, time - origin);
    seen.push(time);
  }
  const total = times.length === 0 ? 0 : Math.max(...times) - first;
  return { gaps, since, total };
}

const timing = (): Performance | null =>
  typeof performance !== 'undefined' && typeof performance.mark === 'function' ? performance : null;

/** Marks one phase of the current peek open. */
export function markPeek(phase: PeekPhase): void {
  try {
    timing()?.mark(peekMarkName(phase));
  } catch {
    // User Timing is a measurement aid only; an open never fails on it.
  }
}

/** Clears the previous open's marks (`open()` does it first). */
export function clearPeekTrace(): void {
  const perf = timing();
  if (!perf) return;
  try {
    for (const phase of PEEK_PHASES) perf.clearMarks(peekMarkName(phase));
  } catch {
    // As above.
  }
}

/** The current open's marks, as User Timing holds them. */
export function peekMarks(): PeekMark[] {
  const perf = timing();
  if (!perf || typeof perf.getEntriesByType !== 'function') return [];
  return perf
    .getEntriesByType('mark')
    .filter((entry) => entry.name.startsWith(PEEK_MARK_PREFIX))
    .map((entry) => ({ name: entry.name, startTime: entry.startTime }));
}

/** Calls `done` once the next frame has painted (a frame, then a task); returns the cancel. */
export function afterNextPaint(done: () => void): () => void {
  let live = true;
  const frame = requestAnimationFrame(() => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      channel.port1.close();
      if (live) done();
    };
    channel.port2.postMessage(null);
  });
  return () => {
    live = false;
    cancelAnimationFrame(frame);
  };
}
